import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiConfigs } from '../../../api/APIConfigs';
import { RhEvent } from '../../../api/models/RhEvent';
import { routes } from '../../../route-list';
import { useRequest } from '../../../util/use-request';
import { RhTab } from '../../core/RhTabs';
import { EventsScreen } from '../components/EventsScreen';

export type EventsProps = {
  // Inputs
  /** current tab id on the uri */
  currentTabId: string;
  /** tabs of this view */
  tabs: RhTab[];
  /** list of scheduled events */
  scheduledEvents: RhEvent[];
  /** lsit of past events */
  pastEvents: RhEvent[];
  /** if getting events */
  loading: boolean;
  scheduledPage: number;
  scheduledHasNextPage: boolean;
  pastPage: number;
  pastHasNextPage: boolean;

  // Inputs
  /** called when the user clicks the add button */
  userClickedAddEvent: () => void;
  /** called when the user clicks a tab */
  userSelectedTab: (id: string) => void;
  /** called when user clicks a single event from the list */
  userClickedAnEvent: (id: string) => void;
  /** called when the user edits the search field on the scheduled live events */
  userUpdatedScheduledLiveEventsSearchField: (term: string) => void;
  /** called when the user edits the search field on the past live events */
  userUpdatedPastLiveEventsSearchField: (term: string) => void;
  userClickedScheduledNextPage: () => void;
  userClickedScheduledPrevPage: () => void;
  userClickedPastNextPage: () => void;
  userClickedPastPrevPage: () => void;
};

export const EventsContainer = () => {
  const [currentTabId, setCurrentTabId] = useState('all');
  const [scheduledLiveEventsSearchTerm, setScheduledLiveEventsSearchTerm] = useState('');
  const [pastLiveEventsSearchTerm, setPastLiveEventsSearchTerm] = useState('');
  const [scheduledPage, setScheduledPage] = useState(1);
  const [scheduledHasNextPage, setScheduledHasNextPage] = useState(false);
  const [pastPage, setPastPage] = useState(1);
  const [pastHasNextPage, setPastHasNextPage] = useState(false);
  const [isGettingEvents, setIsGettingEvents] = useState(false);
  const [scheduledEvents, setScheduledEvents] = useState<RhEvent[]>([]);
  const [pastEvents, setPastEvents] = useState<RhEvent[]>([]);

  const request = useRequest();
  const history = useHistory();

  const PAGE_SIZE = 5;
  const SEARCH_DEBOUNCE_MS = 400;
  const scheduledSearchTimeoutRef = useRef<number | null>(null);
  const pastSearchTimeoutRef = useRef<number | null>(null);

  const loadScheduled = useCallback(
    async (page = 1, name?: string) => {
      try {
        setIsGettingEvents(true);
        const offset = (page - 1) * PAGE_SIZE;
        const upcoming = await request<RhEvent[]>(
          apiConfigs.getEvents({
            showOnlyFromCurrentUser: true,
            limit: PAGE_SIZE,
            offset,
            upcomingEvents: true,
            ...(name ? { name } : {}),
          })
        );
        setScheduledEvents(upcoming || []);
        setScheduledHasNextPage((upcoming || []).length === PAGE_SIZE);
        return upcoming || [];
      } catch (e: any) {
        toast.error(e.message || 'Failed to load events!');
        setScheduledEvents([]);
        setScheduledHasNextPage(false);
        return [];
      } finally {
        setIsGettingEvents(false);
      }
    },
    [request]
  );

  const loadPast = useCallback(
    async (page = 1, name?: string) => {
      try {
        setIsGettingEvents(true);
        const offset = (page - 1) * PAGE_SIZE;
        const past = await request<RhEvent[]>(
          apiConfigs.getEvents({
            showEventsFromCurrentUser: true,
            limit: PAGE_SIZE,
            offset,
            pastEvents: true,
            ...(name ? { name } : {}),
          })
        );
        setPastEvents(past || []);
        setPastHasNextPage((past || []).length === PAGE_SIZE);
        return past || [];
      } catch (e: any) {
        toast.error(e.message || 'Failed to load events!');
        setPastEvents([]);
        setPastHasNextPage(false);
        return [];
      } finally {
        setIsGettingEvents(false);
      }
    },
    [request]
  );

  useEffect(() => {
    loadScheduled(1);
    loadPast(1);
  }, [loadPast, loadScheduled]);

  useEffect(() => {
    return () => {
      if (scheduledSearchTimeoutRef.current) window.clearTimeout(scheduledSearchTimeoutRef.current);
      if (pastSearchTimeoutRef.current) window.clearTimeout(pastSearchTimeoutRef.current);
    };
  }, []);

  return (
    <EventsScreen
      loading={isGettingEvents}
      scheduledPage={scheduledPage}
      scheduledHasNextPage={scheduledHasNextPage}
      pastPage={pastPage}
      pastHasNextPage={pastHasNextPage}
      tabs={[
        { id: 'all', title: 'All' },
        { id: 'event-leaders', title: 'Event Leaders' },
        { id: 'content-creators', title: 'Content Creators' },
        { id: 'church-admins', title: 'Church Admins' },
      ]}
      scheduledEvents={scheduledEvents}
      pastEvents={pastEvents}
      currentTabId={currentTabId}
      userClickedAddEvent={() => {
        history.push(routes.EVENTS__ADD);
      }}
      userSelectedTab={id => {
        setCurrentTabId(id);
      }}
      userClickedAnEvent={id => {
        history.push(routes.EVENTS__VIEW(id));
      }}
      userUpdatedPastLiveEventsSearchField={term => {
        setPastLiveEventsSearchTerm(term);
        setPastPage(1);
        if (pastSearchTimeoutRef.current) window.clearTimeout(pastSearchTimeoutRef.current);
        pastSearchTimeoutRef.current = window.setTimeout(() => {
          loadPast(1, term.trim() || undefined);
        }, SEARCH_DEBOUNCE_MS);
      }}
      userUpdatedScheduledLiveEventsSearchField={term => {
        setScheduledLiveEventsSearchTerm(term);
        setScheduledPage(1);
        if (scheduledSearchTimeoutRef.current) window.clearTimeout(scheduledSearchTimeoutRef.current);
        scheduledSearchTimeoutRef.current = window.setTimeout(() => {
          loadScheduled(1, term.trim() || undefined);
        }, SEARCH_DEBOUNCE_MS);
      }}
      userClickedScheduledNextPage={async () => {
        const next = scheduledPage + 1;
        await loadScheduled(next, scheduledLiveEventsSearchTerm.trim() || undefined);
        setScheduledPage(next);
      }}
      userClickedScheduledPrevPage={async () => {
        const prev = Math.max(1, scheduledPage - 1);
        await loadScheduled(prev, scheduledLiveEventsSearchTerm.trim() || undefined);
        setScheduledPage(prev);
      }}
      userClickedPastNextPage={async () => {
        const next = pastPage + 1;
        await loadPast(next, pastLiveEventsSearchTerm.trim() || undefined);
        setPastPage(next);
      }}
      userClickedPastPrevPage={async () => {
        const prev = Math.max(1, pastPage - 1);
        await loadPast(prev, pastLiveEventsSearchTerm.trim() || undefined);
        setPastPage(prev);
      }}
    />
  );
};
